import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import { getStats, getGuideStatus, postGuideStep, getWelcomeStatus, postWelcome } from '../api/client';

const CHAT_URL = 'https://t.me/+7ifAK6uVjWZkZDMy';

function openChatLink() {
  if (window.Telegram?.WebApp?.openTelegramLink) {
    window.Telegram.WebApp.openTelegramLink(CHAT_URL);
  } else {
    window.open(CHAT_URL);
  }
}

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

export default function HomePage({ onNavigate }) {
  const [stats, setStats] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [newsVideoPlaying, setNewsVideoPlaying] = useState(false);
  // Стартовый гид: null = грузится (ничего не затемняем), {video_done, chat_done} = статус
  const [guide, setGuide] = useState(null);
  const [guideVideoPlaying, setGuideVideoPlaying] = useState(false);
  // Плашка «включите уведомления»: показываем, если бот так и не смог написать юзеру
  // (отклонил попап write-access). Проверяем с задержкой, чтобы не мигать при первом
  // заходе, пока попап ещё на экране / велком в пути.
  const [notifBanner, setNotifBanner] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      getWelcomeStatus().then(st => setNotifBanner(!st.sent)).catch(() => {});
    }, 5000);
    return () => clearTimeout(t);
  }, []);

  const askWriteAccess = useCallback(() => {
    const recheck = () => {
      postWelcome().catch(() => {});
      setTimeout(() => {
        getWelcomeStatus().then(st => setNotifBanner(!st.sent)).catch(() => {});
      }, 1500);
    };
    // Гоча старых клиентов: методы WebApp могут бросить — только try/catch спасает
    try {
      const tg = window.Telegram?.WebApp;
      if (tg && tg.requestWriteAccess) tg.requestWriteAccess(recheck);
      else recheck();
    } catch (e) { recheck(); }
  }, []);

  const load = useCallback(() => {
    getStats().then(setStats).catch(console.error);
  }, []);

  useEffect(() => {
    getGuideStatus()
      .then(setGuide)
      // при ошибке считаем гид пройденным, чтобы не блокировать главную
      .catch(() => setGuide({ video_done: true, chat_done: true }));
  }, []);

  const markGuideStep = useCallback((step) => {
    setGuide(g => ({ ...(g || {}), [step === 'video' ? 'video_done' : 'chat_done']: true }));
    postGuideStep(step).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  const s = stats || {};

  // Гид активен, пока не отмечена видео-инструкция (пока грузится — не показываем и не затемняем)
  const guideActive = !!guide && !guide.video_done;
  const guideVideoId = s.instruction_video ? getYouTubeId(s.instruction_video.url) : null;

  const card = {
    background: 'var(--bg-card)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border)',
    overflow: 'hidden',
  };

  return (
    <div>
      <Header title={
        <img src="/logo.png" alt="Fix-Hub" style={{ height: 26, objectFit: 'contain', display: 'block', filter: 'drop-shadow(0 1px 8px rgba(255,255,255,0.10))' }} />
      } />
      <div style={{ padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Стартовый гид: 2 шага для новичка (видео-инструкция → чат клуба) */}
        {guideActive && (
          <div style={{
            ...card, padding: 16,
            borderColor: 'rgba(255,204,0,0.5)',
            background: 'linear-gradient(160deg, rgba(255,204,0,0.10), rgba(255,204,0,0.02))',
            animation: 'fadeInUp 0.3s ease both',
          }}>
            <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 4 }}>👋 Добро пожаловать в клуб!</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.4 }}>
              Посмотрите короткую видео-инструкцию — в ней всё, как пользоваться приложением:
            </div>

            {/* Видео-инструкция + галочка */}
            <div style={{
              background: 'var(--bg-card)', borderRadius: 12,
              border: '1px solid rgba(255,204,0,0.35)',
              overflow: 'hidden',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px' }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                  background: '#FFCC00',
                  color: '#0A0A0A',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="#0A0A0A"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Видео-инструкция</div>
              </div>
              {guideVideoId && (
                !guideVideoPlaying ? (
                  <div onClick={() => setGuideVideoPlaying(true)} style={{ position: 'relative', cursor: 'pointer' }}>
                    <img
                      src={`https://img.youtube.com/vi/${guideVideoId}/hqdefault.jpg`}
                      alt=""
                      style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }}
                    />
                    <div style={{
                      position: 'absolute', inset: 0,
                      background: 'linear-gradient(transparent 40%, rgba(0,0,0,0.7))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <div style={{
                        width: 56, height: 56, borderRadius: '50%',
                        background: 'rgba(255,204,0,0.9)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                      }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="#0A0A0A"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                      </div>
                    </div>
                  </div>
                ) : (
                  <iframe
                    src={`https://www.youtube.com/embed/${guideVideoId}?autoplay=1&rel=0&modestbranding=1`}
                    title={s.instruction_video.title}
                    style={{ width: '100%', aspectRatio: '16/9', border: 'none', display: 'block' }}
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                )
              )}
              <div onClick={() => markGuideStep('video')} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', cursor: 'pointer',
                borderTop: '1px solid var(--border)',
              }}>
                <div style={{
                  width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                  border: '2px solid rgba(255,204,0,0.7)',
                }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent)' }}>Я ознакомился с инструкцией</span>
              </div>
            </div>
          </div>
        )}

        {/* Уведомления от бота не включены — мягко доспрашиваем, один тап */}
        {notifBanner && !guideActive && (
          <div onClick={askWriteAccess} style={{
            ...card, padding: '12px 14px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 12,
            borderColor: 'rgba(10,132,255,0.45)',
            background: 'linear-gradient(160deg, rgba(10,132,255,0.12), rgba(10,132,255,0.03))',
            animation: 'fadeInUp 0.3s ease both',
          }}>
            <div style={{ fontSize: 22, flexShrink: 0 }}>🔔</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Включите уведомления</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.35 }}>
                Чтобы не пропустить объекты, события и ответы экспертов
              </div>
            </div>
            <div style={{
              fontSize: 13, fontWeight: 700, flexShrink: 0,
              color: '#0A84FF', padding: '8px 12px',
              background: 'rgba(10,132,255,0.15)', borderRadius: 10,
            }}>Включить</div>
          </div>
        )}

        {/* Остальной контент: приглушён, пока гид не пройден */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 12,
          opacity: guideActive ? 0.25 : 1,
          pointerEvents: guideActive ? 'none' : 'auto',
          transition: 'opacity 0.4s ease',
        }}>

        {/* Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, animation: 'fadeInUp 0.3s ease both' }}>
          {[
            { num: s.partners || s.members || '—', label: 'Участников', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg> },
            { num: s.experts || '—', label: 'Экспертов', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
            { num: s.shops || '—', label: 'Магазинов', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
          ].map((c, i) => (
            <div key={i} style={{
              ...card, padding: '14px 10px', textAlign: 'center',
              cursor: 'default',
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6, opacity: 0.85 }}>{c.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent)', letterSpacing: -0.5 }}>{c.num}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginTop: 3 }}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* Chat Button */}
        <div onClick={openChatLink}
          style={{
            ...card, padding: '18px 16px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 14,
            borderColor: 'rgba(10,132,255,0.35)',
            background: 'linear-gradient(135deg, rgba(10,132,255,0.10), rgba(10,132,255,0.02))',
            animation: 'fadeInUp 0.3s ease 0.02s both',
          }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: 'rgba(10,132,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0A84FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Чат клуба</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>Общение, вопросы, нетворкинг</div>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 10px', borderRadius: 20,
            background: 'rgba(48,209,88,0.12)',
          }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--success)', animation: 'pulseDot 1.4s ease infinite' }}></span>
            <span style={{ fontSize: 13, color: 'var(--success)', fontWeight: 700 }}>online</span>
          </div>
        </div>

        {/* Seminar Banner — 24 июля, Кишинёв */}
        <div onClick={() => {
          const url = 'https://seminar.fix-hub.md/';
          window.Telegram?.WebApp?.openLink ? window.Telegram.WebApp.openLink(url) : window.open(url, '_blank');
        }} style={{
          ...card, padding: 16, cursor: 'pointer',
          borderColor: 'rgba(255,204,0,0.4)',
          background: 'linear-gradient(135deg, rgba(255,204,0,0.12), rgba(255,204,0,0.03))',
          animation: 'fadeInUp 0.3s ease 0.02s both',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{
              fontSize: 11, fontWeight: 800, padding: '4px 10px', borderRadius: 6,
              background: '#FFCC00', color: '#0A0A0A', letterSpacing: 0.5,
            }}>СЕМИНАР</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#48484A" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8, lineHeight: 1.3 }}>Встреча строителей Молдовы</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
            <span style={{ color: 'var(--accent)', fontWeight: 700 }}>24 июля, 14:00</span>
            <span>Кишинёв · Unity Tower</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.4 }}>
            Заказчики, партнёры и команда — вживую. Регистрация и программа →
          </div>
        </div>

        {/* LIVE Event */}
        {s.live_event && (
          <div onClick={() => onNavigate('events')} style={{
            ...card, padding: 16, cursor: 'pointer',
            borderColor: 'rgba(255,59,48,0.35)',
            animation: 'fadeInUp 0.3s ease 0.04s both',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{
                fontSize: 11, fontWeight: 800, padding: '4px 10px', borderRadius: 6,
                background: '#FF453A', color: '#fff', letterSpacing: 0.5,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff', animation: 'pulseDot 1.2s ease infinite' }}></span>
                LIVE
              </span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#48484A" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, lineHeight: 1.3 }}>{s.live_event.title}</div>
            <div style={{ display: 'flex', gap: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
              {s.live_event.speaker && <span>{s.live_event.speaker}</span>}
              <span>{s.live_event.registrations_count || 0} участников</span>
            </div>
          </div>
        )}

        {/* Latest Object */}
        {s.latest_object && (
          <div onClick={() => onNavigate('objects')} style={{
            ...card, padding: 16, cursor: 'pointer',
            animation: 'fadeInUp 0.3s ease 0.08s both',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, background: 'var(--accent-dim)', color: 'var(--accent)', letterSpacing: 0.3 }}>Новый объект</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#48484A" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, lineHeight: 1.3 }}>{s.latest_object.title}</div>
            <div style={{ display: 'flex', gap: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
              <span>{s.latest_object.city}</span>
              {s.latest_object.budget > 0 && <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{s.latest_object.budget.toLocaleString()} MDL</span>}
            </div>
          </div>
        )}

        {/* News Video */}
        {s.news_video && getYouTubeId(s.news_video.url) && (
          <div style={{
            ...card,
            borderColor: 'rgba(255,59,48,0.2)',
            animation: 'fadeInUp 0.3s ease 0.12s both',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', top: 12, left: 12, zIndex: 2,
              background: '#FF453A', color: '#fff',
              fontSize: 10, fontWeight: 800, letterSpacing: 0.8,
              padding: '4px 10px', borderRadius: 6,
            }}>НОВОЕ</div>
            {!newsVideoPlaying ? (
              <div onClick={() => setNewsVideoPlaying(true)} style={{ position: 'relative', cursor: 'pointer' }}>
                <img
                  src={`https://img.youtube.com/vi/${getYouTubeId(s.news_video.url)}/hqdefault.jpg`}
                  alt=""
                  style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }}
                />
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(transparent 40%, rgba(0,0,0,0.7))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: 'rgba(255,69,58,0.9)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 20px rgba(255,69,58,0.4)',
                  }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                  </div>
                </div>
                <div style={{ padding: '12px 16px' }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{s.news_video.title}</div>
                </div>
              </div>
            ) : (
              <div>
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(s.news_video.url)}?autoplay=1&rel=0&modestbranding=1`}
                  title={s.news_video.title}
                  style={{ width: '100%', aspectRatio: '16/9', border: 'none', display: 'block' }}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
                <div style={{ padding: '10px 16px' }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{s.news_video.title}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Instruction Video */}
        {s.instruction_video && getYouTubeId(s.instruction_video.url) && (
          <div style={{
            ...card,
            borderColor: 'rgba(255,204,0,0.2)',
            animation: 'fadeInUp 0.3s ease 0.14s both',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', top: 12, left: 12, zIndex: 2,
              background: '#FFCC00', color: '#0A0A0A',
              fontSize: 10, fontWeight: 800, letterSpacing: 0.8,
              padding: '4px 10px', borderRadius: 6,
            }}>ИНСТРУКЦИЯ</div>
            {!videoPlaying ? (
              <div onClick={() => setVideoPlaying(true)} style={{ position: 'relative', cursor: 'pointer' }}>
                <img
                  src={`https://img.youtube.com/vi/${getYouTubeId(s.instruction_video.url)}/hqdefault.jpg`}
                  alt=""
                  style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }}
                />
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(transparent 40%, rgba(0,0,0,0.7))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: 'rgba(255,204,0,0.9)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                  }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="#0A0A0A"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                  </div>
                </div>
                <div style={{ padding: '12px 16px' }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{s.instruction_video.title}</div>
                </div>
              </div>
            ) : (
              <div>
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(s.instruction_video.url)}?autoplay=1&rel=0&modestbranding=1`}
                  title={s.instruction_video.title}
                  style={{ width: '100%', aspectRatio: '16/9', border: 'none', display: 'block' }}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
                <div style={{ padding: '10px 16px' }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{s.instruction_video.title}</span>
                </div>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
